import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLang } from "@/i18n";

export default function ConfirmPlacementDialog({ open, onOpenChange, player, players, onConfirmed }) {
  const { tf } = useLang();
  const navigate = useNavigate();
  const [playerId, setPlayerId] = useState(player?.id || "");
  const [candidates, setCandidates] = useState(players || []);
  const [club, setClub] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("__none__");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [objective, setObjective] = useState("");
  const [genSchedule, setGenSchedule] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlayerId(player?.id || "");
    setClub(player?.club || "");
    setDate(new Date().toISOString().slice(0, 10));
    setContractId("__none__"); setStartDate(""); setEndDate(""); setObjective("");
    setGenSchedule(true);
    if (!players) {
      api.get("/players").then((r) => setCandidates((r.data || []).filter((p) => p.status !== "Placé")))
        .catch(() => setCandidates([]));
    } else setCandidates(players);
  }, [open, player, players]);

  const selected = useMemo(
    () => candidates.find((p) => p.id === playerId) || player || null,
    [candidates, playerId, player]
  );

  useEffect(() => {
    if (!open || !playerId) { setContracts([]); return; }
    if (selected && !club) setClub(selected.club || "");
    api.get(`/contracts?player_id=${playerId}`)
      .then((r) => setContracts(Array.isArray(r.data) ? r.data : (r.data?.contracts || r.data?.items || [])))
      .catch(() => setContracts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playerId]);

  useEffect(() => {
    const c = contracts.find((x) => x.id === contractId);
    if (c) {
      if (c.start_date) setStartDate(c.start_date.slice(0, 10));
      if (c.end_date) setEndDate(c.end_date.slice(0, 10));
      if (c.counterparty && !club) setClub(c.counterparty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const confirm = async () => {
    if (!playerId) { toast.error(tf("Sélectionnez un athlète")); return; }
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.post("/placements/confirm", {
        player_id: playerId,
        club: club.trim(),
        placement_date: date,
        contract_id: contractId === "__none__" ? null : contractId,
        start_date: startDate || null,
        end_date: endDate || null,
        objective: objective.trim() || null,
        generate_schedule: genSchedule,
      });
      const plc = r.data?.placement;
      if (r.data?.already_exists) {
        toast.info(tf("Un placement actif existe déjà pour cet athlète"), {
          action: { label: tf("Voir le placement"), onClick: () => navigate(`/placements/${plc.id}`) },
        });
      } else {
        toast.success(tf("Placement confirmé"), {
          action: { label: tf("Voir le placement"), onClick: () => navigate(`/placements/${plc.id}`) },
        });
      }
      onOpenChange(false);
      onConfirmed?.(plc);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tf("Impossible de confirmer le placement"));
    } finally { setSaving(false); }
  };

  const inputCls = "bg-background/60 border-border dark:border-border";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[88vh] overflow-y-auto" data-testid="confirm-placement-dialog">
        <DialogHeader>
          <DialogTitle>{tf("Confirmer le placement")}</DialogTitle>
          <DialogDescription>
            {tf("L'athlète passera au statut « Placé » et son suivi post-placement sera activé.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Athlète")}</Label>
            {player ? (
              <div className="h-10 flex items-center px-3 rounded-md border border-border dark:border-border bg-secondary/40 text-sm font-medium" data-testid="cp-player-fixed">
                {player.name}
              </div>
            ) : (
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger className={inputCls} data-testid="cp-player-select"><SelectValue placeholder={tf("Sélectionner…")} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {[...candidates].sort((a, b) => (a.status === "Mandat signé" ? -1 : 1) - (b.status === "Mandat signé" ? -1 : 1))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.status === "Mandat signé" ? " · Mandat signé" : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1.5 block">{tf("Club / organisation")}</Label>
              <Input value={club} onChange={(e) => setClub(e.target.value)} className={inputCls} data-testid="cp-club" />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">{tf("Date du placement")}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="cp-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Contrat associé")}</Label>
            <Select value={contractId} onValueChange={setContractId}>
              <SelectTrigger className={inputCls} data-testid="cp-contract"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{tf("Aucun contrat lié")}</SelectItem>
                {contracts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.title || c.counterparty || c.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1.5 block">{tf("Date de début")}</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} data-testid="cp-start" />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">{tf("Date de fin")}</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} data-testid="cp-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Objectif du placement")}</Label>
            <Textarea value={objective} onChange={(e) => setObjective(e.target.value)} className={inputCls}
              placeholder={tf("Ex. : obtenir davantage de temps de jeu avant une montée de niveau.")} data-testid="cp-objective" />
          </div>
          <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="cp-schedule-toggle">
            <Checkbox checked={genSchedule} onCheckedChange={(v) => setGenSchedule(!!v)} />
            {tf("Générer le programme de suivi par défaut (J+7, J+30, J+60, J+90…)")}
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cp-cancel">{tf("Annuler")}</Button>
          <Button onClick={confirm} disabled={saving || !playerId}
            className="bg-foreground hover:bg-foreground/90 text-background" data-testid="cp-confirm">
            {saving ? tf("Confirmation…") : tf("Confirmer le placement")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
